/**
 * Canvas-native activity layout — zoom-stable rows with continuous relaxation
 * (WI #5531, follow-up to the 5390 lane-mode experiments).
 *
 * Why this exists: the store lane packing (`remapStore.activityLanes`) resolves
 * horizontal label crowding in *screen* space (`scaleX(pos)` vs a constant label
 * allowance), so the discrete lane assignment is a function of zoom — activities
 * visibly jump between lanes while zooming. The 5390 "converge" branch fixed the
 * jumping by anchoring the structure at minZoom and blending toward the
 * workstream centre, but its converged targets ignore free vertical space
 * (singletons sit exactly on the centre line, pair-holds expire and are capped
 * at 45/70px), so activities bunch up even when the band is empty.
 *
 * The model here — stable order + octave-blended local layouts — keeps the
 * stability and fixes the bunching. Per workstream:
 *
 * - STABLE ORDER: a greedy first-fit interval packing in minZoom (fully
 *   zoomed-out) space — position × minZoom vs the real measured label reach —
 *   fixes every activity's vertical RANK (packed row, mapped centre-out, the
 *   classic `lanes`-table aesthetic). The rank depends only on data + label
 *   widths, never on the current zoom, so two activities can never swap
 *   vertical places while zooming.
 * - LOCAL LAYOUTS: at a reference zoom, maximal runs of horizontally-crowding
 *   neighbours form groups. A lone activity sits on the centre line (the
 *   converge look); a group spreads evenly around the centre with
 *   banner-friendly spacing (up to MAX_SPACING, capacity-capped), members
 *   ordered by their stable rank. Grouping is LOCAL, so a pair in an empty
 *   stretch uses the free band height instead of inheriting the dense global
 *   grid of the zoomed-out map.
 * - OCTAVE BLENDING: the live layout is the smoothstep interpolation between
 *   the reference layouts at the two powers-of-two zooms surrounding the
 *   current zoom. Reference layouts are fixed within an octave and agree at
 *   the boundaries, so Y is a CONTINUOUS function of zoom — no thresholds, no
 *   permutation jumps, no "labels barely fit so the fan collapses" flips —
 *   and a pair only fully converges to the centre line once its gap clears
 *   ~2× the label reach (the octave span doubles as the comfort margin).
 * - The renderer's draw loop still eases Y toward the target each frame
 *   (exponential approach) to smooth DATA-driven retargets (edits, drags,
 *   resize repacks); something the SVG renderer could never afford
 *   (re-targeting CSS transitions on ~2000 nodes cost +53ms/frame on the
 *   5390 branch).
 *
 * The engine is the single Y source for the canvas world: painters read it via
 * `scene.layout`, interaction (hit-test, gestures, popovers, todo-drag) via
 * `activityLayout.yOf`. It never touches the SVG world; the store getters keep
 * serving the SVG renderer unchanged. The legacy behaviour stays available as
 * the 'classic' mode (featureFlagStore.canvasActivityLayout), where every view
 * method simply delegates to the store getters.
 */
import { useFeatureFlagStore } from '@/store/featureFlagStore';
import { START_X } from '@/store/remapStore';
import { $t } from '@/utils/i18n/vueI18n';
import type { MapStore, SceneContext } from '../engine/SceneContext';
import type { MapActivity } from '@/store/ioStore';

/** Which engine produced the current view. */
export type CanvasLayoutMode = 'flow' | 'classic';

/** One laid-out activity this frame, for neighbourhood queries (banner room). */
export interface LayoutPoint {
  id: string;
  /** Hexagon centre, screen px. */
  x: number;
  y: number;
  /** Right edge of the rendered inline label (screen px), for backward scans. */
  labelReach: number;
}

/**
 * A cluster of same-row activities that cannot render sensibly side by side —
 * hexagons landing on top of each other (capacity-overflow stacks, same-date
 * pile-ups) or packed so tightly that a member could show no readable label —
 * rendered as ONE interactive marker so that no activity is ever invisible,
 * unreachable (PO hard requirement) or anonymous. Detection is a pure read of
 * the settled layout — grouping never feeds back into the layout maths.
 */
export interface LayoutGroup {
  /** Stable identity: the sorted member ids joined with '|'. */
  key: string;
  /** Mean member timeline position (hit-testing projects it live). */
  pos: number;
  /** Marker centre this frame (screen px; y is settled target space). */
  x: number;
  y: number;
  /** Member ids in stable vertical (rank0) order — fan-out + listing order. */
  memberIds: string[];
  /** Limits of the drawn marker centre that keep the whole marker in its band. */
  minY: number;
  maxY: number;
}

/**
 * The per-frame layout view painters and interaction consume. Live frames
 * animate `y`; the export view is settled (y === target).
 */
export interface LayoutView {
  readonly mode: CanvasLayoutMode;
  /**
   * Memo key for derived caches (required-line stagger): changes identity
   * whenever the layout targets change.
   */
  readonly epoch: unknown;
  /** All laid-out activities sorted by screen x ('flow' only, else empty). */
  readonly points: readonly LayoutPoint[];
  /** Collapsed occlusion-group markers to draw this frame ('flow' only). */
  readonly groups: readonly LayoutGroup[];
  /** Members hidden behind a collapsed marker (skip hexagon/label/banner). */
  readonly collapsedIds: ReadonlySet<string>;
  /** Marker under the cursor, for the hover ring (live view only). */
  readonly hoveredGroupKey: string | null;
  /** Marker whose members MapGroupList shows, ringed like a hover (live view only). */
  readonly listedGroupKey: string | null;
  /** The collapsed marker hiding this activity, or null when drawn itself. */
  markerOf(id: string): LayoutGroup | null;
  /** Screen Y of an activity (vertical-move preview and fake-row included). */
  y(id: string): number;
  /**
   * Settled (target-space) Y, ignoring the in-flight easing. Visibility
   * decisions (banner room) use this so they stay stable through a glide.
   */
  targetY(id: string): number;
  /** Screen X where this activity's inline label must stop, or Infinity. */
  labelLimitX(id: string): number;
  /** Same-row grouping key (required-line elbow stagger). */
  rowKey(id: string): number;
}

// Keep activities off the band borders (converge parity: hexagon half-height).
const EDGE_PAD = 14;
// Minimum vertical separation between fan rows (hexagon 28px + clearance).
const MIN_ROW_SEP = 32;
// Horizontal clearance two hexagons need even with labels hidden (30px + gap).
const MIN_X_CLEAR = 36;
// Two hexagons (30px wide) sharing a row visually merge within this Δx — the
// slot assignment dodges such pairs onto neighbouring rows when capacity
// forces sharing, so an activity is never parked invisibly behind another.
const OCCLUDE_X = 30;
// Preferred vertical spacing between group members: two banner cards (60px
// tall) clear each other with air to spare, so fanned activities can BOTH
// show full banners where the band height allows.
const MAX_SPACING = 90;
// Label reach past the measured title: hexagon→pill 18 + glyph 17 + margin 15.
const LABEL_PAD = 50;
// Banner-card horizontal chrome past its widest content row: paddings
// 10 + 15 + state-glyph column 20 + 10 (BannerPainter.bannerWidth).
const CARD_CHROME = 55;
// Horizontal room a card needs beyond its own width: 35px hexagon→card offset
// + 35px forward clearance (mirrors hasBannerRoomFlow's rightEdge).
const CARD_CLEAR = 70;
// Cap a single title's influence on the layout (it still truncates normally).
const LABEL_W_CAP = 320;
// Two labels share a row (truncate against each other) within this ΔY.
const ROW_TOL = 20;
// Y-easing time constant; ~95% settled after 3τ.
const ANIM_TAU_MS = 90;
// Snap-to-target distance that ends the animation.
const SNAP_EPS = 0.5;
const LABEL_FONT = '11px Roboto, sans-serif';
// Two activities count as occluding (→ group marker) when their hexagon
// centres land this close in settled space: |Δx| under a hexagon width and
// |Δy| under ROW_TOL (the layout's own "same row" notion) means one blob.
// With labels hidden this bare-hexagon overlap is the only grouping trigger.
const GROUP_X_EPS = 30;
const GROUP_Y_EPS = 20;
// REALLY hidden (the toggle-independent trigger): centres within half a
// hexagon in BOTH axes — the back activity is mostly covered and effectively
// unreachable. This is the pigeonhole residue (same date, no free row) the
// slot dodge cannot separate; partial overlaps stay individual when the
// grouping toggle is off.
const HIDDEN_EPS = 15;
// Hexagons are 28px tall: centres closer than this merge into one blob even
// across DIFFERENT rows — which is exactly the state a slot-sharing pair
// passes through while the octave blend separates it (Δy sweeps 0 → spacing).
// The occlusion trigger uses this wider window so the marker only releases
// members once they are visually distinguishable; genuine adjacent rows never
// enter it (capacity spacing ≥ MIN_ROW_SEP = 32).
const GROUP_Y_OVERLAP = 30;
// With labels shown, grouping also triggers on label starvation: a same-row
// neighbour so close that the left member's label would be SKIPPED ENTIRELY
// by the LabelsPainter (its maxWidth < 6 cutoff) — an unlabeled hexagon is
// anonymous and "does not really make sense" (PO). Pair threshold = label
// chrome (LABEL_PAD + favourite star) + this minimum, ≈56–74px.
//
// HARD CEILING — do not raise above the painter's 6px skip width: the layout
// splits crowd-runs at `reach = titleWidth + chrome`, so any value above the
// narrowest title's measured width makes the pair threshold exceed `reach`,
// and activities the layout itself spaced out as separate runs (consecutive
// centre-line singletons!) start chaining into board-wide groups (the
// 2026-06-10 v1.1 "way too aggressive" revert: 13/16-member markers
// everywhere, fans too tight to label). With ≤ 6 the trigger provably stays
// confined to within-run, same-slot members — the genuinely starved ones.
const GROUP_MIN_LABEL_W = 6;
// A group renders collapsed only once every member's animated Y is this close
// to its settled target — so a closing fan glides shut BEFORE the marker
// swaps in, instead of the members vanishing mid-flight.
const GROUP_SETTLE_EPS = 8;
// Fan-out geometry: the activity hexagon spans ~±15.5 × ±14 (hexPath), and a
// fanned member keeps this much air to anything drawn next to it.
const HEX_HALF_W = 15.5;
const HEX_HALF_H = 14;
// A group marker's stacked hexagon (−4) and count badge reach this far above
// and right of its centre (GroupMarkersPainter).
const MARKER_REACH_UP = 18;
const MARKER_REACH_RIGHT = 23;
// Right edge of the workstream sidebar painted over the scene (START_X − 20).
const SIDEBAR_RIGHT = START_X - 20;
const FAN_GAP = 2;
// Search steps (px) for the fan's row pitch and its shift inside the band.
const FAN_PITCH_STEP = 2;
const FAN_SHIFT_STEP = 2;

/** Targets and derived per-frame data for the 'flow' engine. */
interface FlowFrame {
  targets: Map<string, number>;
  rows: Map<string, number>;
  limits: Map<string, number>;
  points: LayoutPoint[];
  groups: LayoutGroup[];
  /** The expanded group whose members are fanned out this frame, if any. */
  fanned: string | null;
  /**
   * The expanded group when its members do not fit into a fan inside their
   * band: its marker stays drawn and the members are listed instead. Only
   * while the marker's centre is on screen (where it can be clicked) — a list
   * never floats at the map's edge for a group scrolled out of view.
   */
  listed: LayoutGroup | null;
}

/** Screen-px box of something drawn on the map that a fanned member must not cover. */
export interface FanObstacle {
  l: number;
  r: number;
  t: number;
  b: number;
}

/** The box of an activity hexagon centred at (x, y). */
export function hexagonBox(x: number, y: number): FanObstacle {
  return {
    l: x - HEX_HALF_W,
    r: x + HEX_HALF_W,
    t: y - HEX_HALF_H,
    b: y + HEX_HALF_H,
  };
}

/**
 * The box of a collapsed group marker centred at (x, y): the front hexagon,
 * the stacked one behind it (+4, −4) and the two-digit count badge at the
 * top-right corner (GroupMarkersPainter geometry).
 */
export function markerBox(x: number, y: number): FanObstacle {
  return {
    l: x - HEX_HALF_W,
    r: x + MARKER_REACH_RIGHT,
    t: y - MARKER_REACH_UP,
    b: y + HEX_HALF_H,
  };
}

/**
 * Where a collapsed marker is drawn vertically: the mean of its members'
 * (animated) Ys, kept inside its band. The marker's stacked hexagon and count
 * badge reach 18px above its centre, so on the band's top row the plain mean
 * would poke them 4px into the workstream above. Painter, hit-test, connector
 * anchors, lasso and the member list all go through here, so they agree.
 */
export function markerY(
  group: LayoutGroup,
  yOf: (id: string) => number,
): number {
  let y = 0;
  for (const id of group.memberIds) {
    y += yOf(id);
  }

  return Math.min(group.maxY, Math.max(group.minY, y / group.memberIds.length));
}

/**
 * Drawn-centre limits for a marker in a band at [wsY, wsY + wsH]. A band
 * thinner than the marker (32px) cannot hold it: the marker then sits where
 * it overflows the band equally at the top and at the bottom.
 */
function markerLimits(
  wsY: number,
  wsH: number,
): { minY: number; maxY: number } {
  const minY = wsY + MARKER_REACH_UP;
  const maxY = wsY + wsH - HEX_HALF_H;
  const mid = (minY + maxY) / 2;

  return minY <= maxY ? { minY, maxY } : { minY: mid, maxY: mid };
}

/**
 * Rows for a group fanned out by a marker click: one Y per member (members in
 * stable rank order, top → bottom, each at its own x), all inside the band's
 * centre range [top, bottom], evenly spaced and at least MIN_ROW_SEP apart:
 * no two hexagons overlap, and the 60px banner card of a hovered or selected
 * member (±30px) never covers the centre of another one — also not in a run of
 * different dates, where the next member sits right of it, under its card.
 * No member covers an obstacle (another group's marker or a visible hexagon).
 * The widest pitch wins (up to MAX_SPACING, so banners fit when the band
 * allows), the fan as close to the band centre as the obstacles let it.
 * Without obstacles this is the evenly spread, centred fan.
 *
 * Returns null when the band cannot hold such a fan — it is too thin for the
 * member count, or the obstacles leave no room. The caller then lists the
 * members instead of squeezing hexagons onto each other or across the band
 * border into the neighbouring workstream.
 */
export function planFanRows(
  xs: readonly number[],
  obstacles: readonly FanObstacle[],
  top: number,
  bottom: number,
): number[] | null {
  const n = xs.length;
  const range = bottom - top;
  if (n < 2) {
    return xs.map(() => top + range / 2);
  }
  if (range < (n - 1) * MIN_ROW_SEP) {
    return null;
  }
  const covers = (x: number, y: number, o: FanObstacle): boolean =>
    x + HEX_HALF_W + FAN_GAP > o.l &&
    x - HEX_HALF_W - FAN_GAP < o.r &&
    y + HEX_HALF_H + FAN_GAP > o.t &&
    y - HEX_HALF_H - FAN_GAP < o.b;
  // Only obstacles that share an x stretch with some member can ever collide.
  const near = obstacles.filter((o) =>
    xs.some(
      (x) => x + HEX_HALF_W + FAN_GAP > o.l && x - HEX_HALF_W - FAN_GAP < o.r,
    ),
  );
  const widest = Math.min(MAX_SPACING, range / (n - 1));
  const pitches: number[] = [];
  for (let p = widest; p > MIN_ROW_SEP; p -= FAN_PITCH_STEP) {
    pitches.push(p);
  }
  pitches.push(MIN_ROW_SEP);
  for (const pitch of pitches) {
    const slack = range - (n - 1) * pitch;
    // Shifts of the fan's first row from the centred position: 0, ±2, ±4 …
    // and the two extremes, so a fan can also hug the band's top or bottom.
    const shifts = [0];
    for (let s = FAN_SHIFT_STEP; s < slack / 2; s += FAN_SHIFT_STEP) {
      shifts.push(s, -s);
    }
    if (slack > 0) {
      shifts.push(slack / 2, -slack / 2);
    }
    for (const shift of shifts) {
      const start = top + slack / 2 + shift;
      let fits = true;
      for (let k = 0; k < n && fits; k++) {
        const y = start + k * pitch;
        for (const o of near) {
          if (covers(xs[k], y, o)) {
            fits = false;
            break;
          }
        }
      }
      if (fits) {
        return xs.map((_, k) => start + k * pitch);
      }
    }
  }

  return null;
}

/**
 * Centre-out row permutation: packing row r (0 = busiest) maps to visual slot
 * mid, mid+1, mid−1, mid+2, … so the fullest row sits on the centre line and
 * later rows alternate outward (matches the store `lanes` table for 1–6 rows).
 */
const permCache = new Map<number, number[]>();
function centerOutPerm(rowCount: number): number[] {
  const cached = permCache.get(rowCount);
  if (cached) {
    return cached;
  }
  const mid = Math.floor((rowCount - 1) / 2);
  const perm: number[] = [mid];
  for (let step = 1; perm.length < rowCount; step++) {
    if (mid + step < rowCount) {
      perm.push(mid + step);
    }
    if (perm.length < rowCount && mid - step >= 0) {
      perm.push(mid - step);
    }
  }
  permCache.set(rowCount, perm);

  return perm;
}

/** Hermite smoothstep on [0,1] — the σ taper between row-hold and release. */
function smoothstep(t: number): number {
  const c = Math.min(1, Math.max(0, t));

  return c * c * (3 - 2 * c);
}

/** The title as the label renders it (mirrored suffix included). */
function labelTitle(activity: MapActivity): string {
  return activity.mirrored
    ? `${activity.title} || ${activity.sourceProjectTitle ?? ''}`
    : activity.title;
}

/** Store-delegating Y with the vertical-move preview + fake-row semantics. */
function storeY(map: MapStore, id: string): number {
  return map.getActivityY(id);
}

/**
 * Compute the flow targets for the current zoom/data. Pure derivation — all
 * inputs come from the scene (store getters + cached text measurement).
 * `keepFan`: the expanded group is fanned out already, so its members need not
 * all be on screen to stay fanned (see ActivityLayoutEngine.beginFrame).
 */
function computeFlowFrame(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
  expandedKey: string | null,
  keepFan = false,
): FlowFrame {
  const { map, io } = scene;
  const targets = new Map<string, number>();
  const rows = new Map<string, number>();
  const limits = new Map<string, number>();
  const points: LayoutPoint[] = [];
  const groups: LayoutGroup[] = [];
  let fanned: string | null = null;
  let listed: LayoutGroup | null = null;

  // Labels hidden entirely → only hexagon clearance drives the clustering
  // (mirrors MapActivityLabel.showLabel: 'none' hides unless a link is selected).
  const labelsShown =
    map.labelMode !== 'none' || map.selectedContext === 'link';

  for (const ws of io.workstreams) {
    // WI #5563: `isHiddenOnMap` already reflects the temporary expand (solo),
    // so a non-expanded workstream is skipped here too. WI #5596:
    // filter-hidden workstreams are out of the layout the same way.
    if (ws.isHiddenOnMap || map.filterHiddenWorkstreamIds.has(ws.id)) {
      // Fake-row placement comes from the store fallback in `y()`.
      continue;
    }
    // WI #5596: filter-hidden activities leave the lane layout entirely, so
    // the remaining matches re-flow into the freed space; their link anchors
    // come from the store fallback in `y()` (real date-X / workstream-Y).
    const members = io
      .activitiesInWorkstream(ws.id)
      .filter(
        (a) =>
          !a.isHiddenOnMap &&
          !map.isHiddenMirroredActivity(a.id) &&
          !map.isFilterHiddenActivityId(a.id),
      ) as MapActivity[];
    if (!members.length) {
      continue;
    }

    const wsY = map.workstreamY(ws.id);
    const wsH = map.workstreamHeight(ws.id);
    const center = wsY + wsH / 2;
    const availH = Math.max(0, wsH - 2 * EDGE_PAD);
    const maxRows = Math.max(1, Math.floor(availH / MIN_ROW_SEP) + 1);

    // Stable x-order: activityPos is drag/spacing/compress-aware; ties keep the
    // store's originalPosition+sequence order (Array.sort is stable).
    const sorted = members
      .map((a) => ({ a, pos: map.activityPos(a) }))
      .sort((m, n) => m.pos - n.pos);

    // How much horizontal room each member needs before its right neighbour.
    // `reach` (title capped at LABEL_W_CAP) drives the cluster predicate so one
    // huge title can't dominate the layout; `fullReach` (uncapped) is the true
    // rendered label extent, used for truncation limits and banner-room scans.
    // `chrome` is the title-independent label overhead (pill chrome + star) —
    // the grouping kill-zone is chrome + GROUP_MIN_LABEL_W.
    const reach: number[] = new Array(sorted.length);
    const fullReach: number[] = new Array(sorted.length);
    const chrome: number[] = new Array(sorted.length);
    for (let i = 0; i < sorted.length; i++) {
      if (!labelsShown) {
        reach[i] = MIN_X_CLEAR;
        fullReach[i] = MIN_X_CLEAR;
        chrome[i] = 0;
        continue;
      }
      const a = sorted[i].a;
      const w = scene.text.measure(ctx, labelTitle(a), LABEL_FONT);
      const extra =
        LABEL_PAD + (!map.isTemplate && !a.mirrored && a.isFavorite ? 18 : 0);
      reach[i] = Math.max(MIN_X_CLEAR, Math.min(w, LABEL_W_CAP) + extra);
      fullReach[i] = Math.max(MIN_X_CLEAR, w + extra);
      chrome[i] = extra;
    }

    // Fan criterion ("when do neighbours deserve different rows"). The room
    // test in hasBannerRoomFlow only excuses neighbours at |ΔY| ≥ 60, so two
    // activities left together on the centre line block each other's banner
    // even in an empty band. In auto label mode (the only mode with room-based
    // banners) a pair therefore crowds while the LEFT member's banner CARD
    // would not fit before the right one — not just its label: fanning them
    // to different rows is what lets both show full banners ("show banner if
    // possible", PO). Card width is approximated from its two dominant rows
    // (title + deadline; the responsible row is almost always narrower), the
    // title capped like the label reach so one huge name can't dominate.
    const bannersAuto =
      map.labelMode === 'auto' && map.selectedContext !== 'link';
    const fanReach: number[] = bannersAuto
      ? new Array<number>(sorted.length)
      : reach;
    if (bannersAuto) {
      // dd.mm.yyyy dates render ~fixed-width: one cached measurement covers
      // every card's deadline row. Templates have no deadline row.
      const deadlineRowW = map.isTemplate
        ? 0
        : scene.text.measure(
            ctx,
            $t('activity.dueDate', { date: '22.22.2222' }),
            LABEL_FONT,
          );
      for (let i = 0; i < sorted.length; i++) {
        const titleW = Math.min(
          scene.text.measure(ctx, labelTitle(sorted[i].a), LABEL_FONT),
          LABEL_W_CAP,
        );
        const cardW = Math.max(titleW, deadlineRowW) + CARD_CHROME;
        fanReach[i] = Math.max(reach[i], cardW + CARD_CLEAR);
      }
    }

    // STEP 1 — stable vertical ORDER: greedy first-fit interval packing in
    // minZoom (fully-zoomed-out) space, the most crowded the map can get. The
    // packed row, mapped centre-out, fixes every activity's vertical RANK —
    // it depends only on data + label widths, never on the current zoom, so
    // two activities can never swap places while zooming.
    const minZoom =
      Number.isFinite(map.minZoom) && map.minZoom > 0 ? map.minZoom : map.zoom;
    const rowEnds: number[] = [];
    const rowOf: number[] = new Array<number>(sorted.length);
    for (let i = 0; i < sorted.length; i++) {
      const begin = sorted[i].pos * minZoom;
      let row = rowEnds.findIndex((end) => end <= begin);
      if (row === -1) {
        if (rowEnds.length < maxRows) {
          row = rowEnds.length;
          rowEnds.push(0);
        } else {
          // Band capacity exhausted: stack onto the row that frees up first
          // (classic overflow behaviour); label truncation absorbs the rest.
          row = 0;
          for (let r = 1; r < rowEnds.length; r++) {
            if (rowEnds[r] < rowEnds[row]) {
              row = r;
            }
          }
        }
      }
      rowOf[i] = row;
      rowEnds[row] = begin + reach[i];
    }
    const perm = centerOutPerm(rowEnds.length);
    // Vertical rank (top = small) at full fan; ties broken by x-order.
    const rank0 = rowOf.map((row) => perm[row]);

    // STEP 2 — a LOCAL layout at a fixed reference zoom: maximal runs of
    // crowding pairs (the right member inside the left one's label reach at
    // zRef) form groups; a lone activity sits on the centre line; a group of m
    // spreads evenly around the centre with banner-friendly spacing (up to
    // MAX_SPACING, capacity-capped), members ordered by their stable rank0.
    // Membership depends on zRef — NOT on the live zoom — so scrubbing the
    // zoom cannot change a reference layout.
    const layoutAtZoom = (zRef: number): number[] => {
      const ys = new Array<number>(sorted.length);
      let start = 0;
      for (let i = 0; i < sorted.length; i++) {
        const isLast = i === sorted.length - 1;
        if (
          !isLast &&
          (sorted[i + 1].pos - sorted[i].pos) * zRef < fanReach[i]
        ) {
          continue;
        }
        const size = i - start + 1;
        const slots = Math.min(size, maxRows);
        const spacing =
          slots > 1 ? Math.min(MAX_SPACING, availH / (slots - 1)) : 0;
        const members: number[] = [];
        for (let q = start; q <= i; q++) {
          members.push(q);
        }
        members.sort((a, b) => rank0[a] - rank0[b] || a - b);
        // Slot assignment: uniform rank-quantile baseline (balanced rows at
        // every density) with a SUCCESS-GATED anti-occlusion dodge. The
        // quantile split is x-blind and can park two SAME-DATE members on one
        // slot (one hexagon fully behind the other, unreachable), so a member
        // may move ONE slot below its baseline — but ONLY when (a) a PRIOR
        // member already placed on its baseline slot x-overlaps it AND (b)
        // the slot below is actually clear for it (no baseline or dodged
        // occupant within a hexagon width). The success gate is what keeps
        // the zoomed-out picture identical to the plain quantile layout: in
        // dense rows the slot below is never clear, so nothing dodges and no
        // row drains. Keeping the FIRST member of an x-pile on its baseline
        // (only later ones dodge) preserves the rank order downward. Members
        // that can't dodge (more same-date members than rows) still stack;
        // that residue is the grouping feature's territory.
        const uniformOf = new Array<number>(size);
        const memberX = new Array<number>(size);
        // Baseline occupancy per slot (ALL members, known up front) — the
        // dodge target must be clear of future baseline arrivals too, else
        // the dodge would just relocate the occlusion onto them.
        const baseXs: number[][] = Array.from({ length: slots }, () => []);
        for (let ord = 0; ord < size; ord++) {
          uniformOf[ord] = Math.floor((ord * slots) / size);
          memberX[ord] = sorted[members[ord]].pos * zRef;
          baseXs[uniformOf[ord]].push(memberX[ord]);
        }
        // Placed-so-far occupancy (baseline AND dodged), in rank order.
        const placedXs: number[][] = Array.from({ length: slots }, () => []);
        for (let ord = 0; ord < size; ord++) {
          const u = uniformOf[ord];
          const x = memberX[ord];
          let s = u;
          if (
            s < slots - 1 &&
            placedXs[u].some((o) => Math.abs(o - x) < OCCLUDE_X) &&
            !baseXs[u + 1].some((o) => Math.abs(o - x) < OCCLUDE_X) &&
            !placedXs[u + 1].some((o) => Math.abs(o - x) < OCCLUDE_X)
          ) {
            s = u + 1;
          }
          placedXs[s].push(x);
          ys[members[ord]] = center + (s - (slots - 1) / 2) * spacing;
        }
        start = i + 1;
      }

      return ys;
    };

    // STEP 3 — octave blending: evaluate the layout at the two powers-of-two
    // reference zooms surrounding the live zoom and interpolate. Y is a
    // continuous function of zoom (reference layouts are fixed within an
    // octave; at an octave boundary the outgoing blend target IS the incoming
    // base), groups adapt to LOCAL crowding (a lone pair in an empty stretch
    // spreads wide enough for two banner cards instead of sharing the global
    // grid), and a pair only fully converges to the centre line once its gap
    // clears ~2× the label reach — the octave span gives the comfort margin
    // intrinsically.
    const ratio = Math.max(1, map.zoom / minZoom);
    const octave = Math.floor(Math.log2(ratio));
    const blend = smoothstep(Math.log2(ratio) - octave);
    const yA = layoutAtZoom(minZoom * 2 ** octave);
    const yB = blend > 0 ? layoutAtZoom(minZoom * 2 ** (octave + 1)) : yA;
    const ty = new Array<number>(sorted.length);
    for (let i = 0; i < sorted.length; i++) {
      ty[i] = yA[i] + (yB[i] - yA[i]) * blend;
      targets.set(sorted[i].a.id, ty[i]);
    }

    // Members that will render collapsed, mapped to their group's marker x:
    // for neighbours' label limits (a label may grow past the invisible
    // member up to the MARKER) and hexagon-only label reach below.
    const collapsedMarkerX = new Map<string, number>();

    // GROUPING — two triggers, each with its own vertical window, read off
    // the settled targets (a deterministic function of data + zoom, never of
    // animation state), chained transitively (A~B, B~C ⇒ one blob):
    //  1a. HARD OCCLUSION (ALWAYS active, toggle ignored — PO): the member is
    //      REALLY hidden — centre within HIDDEN_EPS of a neighbour in both
    //      axes (the pigeonhole residue beyond what the slot dodge can
    //      separate: same date, no free row). An invisible, unreachable
    //      activity must collapse into a marker no matter what.
    //  1b. SOFT OCCLUSION (toggle-gated): hexagons merely visually merge
    //      (GROUP_X_EPS / GROUP_Y_OVERLAP — covers mid-blend partial
    //      overlaps). Off ⇒ partial overlaps render individually.
    //  2.  LABEL STARVATION (labels shown, toggle-gated): the right
    //      neighbour sits inside the left member's label kill-zone (chrome +
    //      GROUP_MIN_LABEL_W), so the left member could render no label at
    //      all — an anonymous hexagon. Collapsing those into a counted
    //      marker reads better than a strip of unlabeled hexagons (PO).
    //      Zooming in dissolves the pair exactly when label room appears.
    // Grouping changes NOTHING about the layout: every member keeps its
    // natural target; painters/hit-tests substitute the marker at draw time.
    // Pan cancels out of the Δx test (pure pos × zoom).
    // An actively-dragged activity stays a singleton: its drag-preview pos
    // would otherwise collapse it into a marker right under the cursor.
    {
      const groupingOn = map.activityGroupingMode !== 'off';
      const starvationOn = labelsShown && groupingOn;
      const dragging =
        (map.isMovingItem || map.isMovingItemVertically) &&
        map.movingContext === 'activity'
          ? new Set(map.movedActivityIds)
          : null;
      // Widest possible pair distance (favourite-starred left member) — the
      // scan-window break; the exact per-pair threshold is checked inside.
      const maxPairPx = starvationOn
        ? Math.max(LABEL_PAD + 18 + GROUP_MIN_LABEL_W, GROUP_X_EPS)
        : groupingOn
          ? GROUP_X_EPS
          : HIDDEN_EPS;
      const clusterOf = new Array<number>(sorted.length).fill(-1);
      const clusters: number[][] = [];
      for (let i = 0; i < sorted.length; i++) {
        if (!dragging?.has(sorted[i].a.id)) {
          for (let j = i - 1; j >= 0; j--) {
            const gapPx = (sorted[i].pos - sorted[j].pos) * map.zoom;
            if (gapPx >= maxPairPx) {
              break;
            }
            const dy = Math.abs(ty[i] - ty[j]);
            // Trigger 1a — HARD OCCLUSION (always on, toggle ignored — PO):
            // the member is REALLY hidden, its centre within half a hexagon
            // of the neighbour in both axes (the pigeonhole residue the slot
            // dodge cannot separate: same date, no free row). An invisible,
            // unreachable activity must become a marker no matter what.
            const hidden = gapPx < HIDDEN_EPS && dy < HIDDEN_EPS;
            // Trigger 1b — SOFT OCCLUSION (toggle-gated): hexagons merely
            // visually merge. The wide Y window (GROUP_Y_OVERLAP) covers the
            // whole mid-blend journey of a separating slot-share, so the
            // marker holds until the members actually read as two hexagons.
            const occluded =
              groupingOn && gapPx < GROUP_X_EPS && dy < GROUP_Y_OVERLAP;
            // Trigger 2 — LABEL STARVATION (labels shown, toggle-gated):
            // same-ROW neighbour inside the label kill-zone. Its Y window
            // stays ROW_TOL-aligned (GROUP_Y_EPS): beyond it the left
            // member's label isn't truncated at all, so nothing is starved.
            const starved =
              starvationOn &&
              gapPx < chrome[j] + GROUP_MIN_LABEL_W &&
              dy < GROUP_Y_EPS;
            if (
              (!hidden && !occluded && !starved) ||
              dragging?.has(sorted[j].a.id)
            ) {
              continue;
            }
            const c = clusterOf[j];
            if (clusterOf[i] === -1) {
              clusterOf[i] = c;
              clusters[c].push(i);
            } else if (clusterOf[i] !== c) {
              // i bridges two clusters — merge c into i's.
              for (const m of clusters[c]) {
                clusterOf[m] = clusterOf[i];
                clusters[clusterOf[i]].push(m);
              }
              clusters[c] = [];
            }
          }
        }
        if (clusterOf[i] === -1) {
          clusterOf[i] = clusters.length;
          clusters.push([i]);
        }
      }
      // What an expanded fan must not cover: the other markers in this band
      // and its visible hexagons near the fan (PO 2026-06-11: expanding the
      // lower of two stacked groups once hid a fanned member exactly behind
      // the upper marker). Everything else on the map lies outside the band.
      const fanObstacles = (cluster: number[]): FanObstacle[] => {
        let minX = Infinity;
        let maxX = -Infinity;
        for (const q of cluster) {
          const x = scene.screenX(sorted[q].pos);
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
        }
        const near = (x: number): boolean =>
          x > minX - 2 * MIN_X_CLEAR && x < maxX + 2 * MIN_X_CLEAR;
        const obstacles: FanObstacle[] = [];
        for (const other of clusters) {
          if (other === cluster || other.length < 2) {
            continue;
          }
          let posSum = 0;
          let ySum = 0;
          for (const q of other) {
            posSum += sorted[q].pos;
            ySum += ty[q];
          }
          const x = scene.screenX(posSum / other.length);
          if (near(x)) {
            const { minY, maxY } = markerLimits(wsY, wsH);
            obstacles.push(
              markerBox(x, Math.min(maxY, Math.max(minY, ySum / other.length))),
            );
          }
        }
        for (let i = 0; i < sorted.length; i++) {
          const c = clusterOf[i];
          if (c !== -1 && clusters[c].length >= 2) {
            continue; // a member of this fan or of another marker
          }
          const x = scene.screenX(sorted[i].pos);
          if (near(x)) {
            obstacles.push(hexagonBox(x, ty[i]));
          }
        }

        return obstacles;
      };

      for (const cluster of clusters) {
        if (cluster.length < 2) {
          continue;
        }
        cluster.sort((a, b) => rank0[a] - rank0[b] || a - b);
        const memberIds = cluster.map((q) => sorted[q].a.id);
        const key = [...memberIds].sort().join('|');
        let posSum = 0;
        let ySum = 0;
        for (const q of cluster) {
          posSum += sorted[q].pos;
          ySum += ty[q];
        }
        const pos = posSum / cluster.length;
        const markerX = scene.screenX(pos);
        const group: LayoutGroup = {
          key,
          pos,
          x: markerX,
          y: ySum / cluster.length,
          memberIds,
          ...markerLimits(wsY, wsH),
        };
        groups.push(group);
        // Click-to-expand fan-out: members spread vertically inside their
        // band (stable rank order, the widest even spacing that fits); the
        // engine easing animates the glide. The overridden targets feed the
        // label/banner pass below, so fanned members get full labels and
        // banner room. Detection above ran on the NATURAL targets, so the fan
        // can never re-trigger or distort grouping.
        // A group opens as a fan only where every member is fully on screen:
        // one that reaches under the workstream sidebar or past the canvas
        // edge would hide members. An open fan stays one however the map is
        // panned (`keepFan`) — its members then scroll away like any hexagon.
        const fanXs = cluster.map((q) => scene.screenX(sorted[q].pos));
        const fanOnScreen =
          keepFan ||
          fanXs.every(
            (x) =>
              x - HEX_HALF_W >= SIDEBAR_RIGHT && x + HEX_HALF_W <= scene.width,
          );
        const fanY =
          key === expandedKey && fanOnScreen
            ? planFanRows(
                fanXs,
                fanObstacles(cluster),
                center - availH / 2,
                center + availH / 2,
              )
            : null;
        if (fanY) {
          fanned = key;
          for (let k = 0; k < cluster.length; k++) {
            targets.set(memberIds[k], fanY[k]);
          }
        } else {
          if (
            key === expandedKey &&
            markerX >= SIDEBAR_RIGHT &&
            markerX <= scene.width
          ) {
            // No room for a fan in this band (too thin for the member count,
            // or other markers/hexagons in the way): the marker stays and
            // the members are listed next to it instead.
            listed = group;
          }
          for (const id of memberIds) {
            collapsedMarkerX.set(id, markerX);
          }
        }
      }
    }

    // Label limits + points, now that every member has a target: a label stops
    // at the next activity to the right that shares its row (|ΔY| < ROW_TOL).
    for (let i = 0; i < sorted.length; i++) {
      const { a, pos } = sorted[i];
      const x = scene.screenX(pos);
      const y = targets.get(a.id) ?? center;
      rows.set(a.id, Math.round(y / 4));
      let limit: number | undefined;
      for (let j = i + 1; j < sorted.length; j++) {
        // A blocker beyond the label's own (uncapped) extent can't truncate
        // anything — stop scanning there.
        if ((sorted[j].pos - pos) * map.zoom > fullReach[i]) {
          break;
        }
        const other = targets.get(sorted[j].a.id) ?? center;
        if (Math.abs(other - y) >= ROW_TOL) {
          continue;
        }
        // A collapsed member is invisible — the visual obstacle is its
        // group's MARKER (the members' mean x), so the label may grow past
        // the vacated spot up to the marker, exactly as it would up to a
        // hexagon. A visible blocker ends the scan (x-sorted: nothing later
        // sits further left); a collapsed one keeps it open — a later
        // same-row blocker can still sit left of this group's marker.
        const markerX = collapsedMarkerX.get(sorted[j].a.id);
        const obstacle = markerX ?? scene.screenX(sorted[j].pos);
        if (limit === undefined || obstacle < limit) {
          limit = obstacle;
        }
        if (markerX === undefined) {
          break;
        }
      }
      if (limit !== undefined) {
        limits.set(a.id, limit);
      }
      // The label extent actually drawn: the natural width, clipped to ~the
      // truncation limit when one applies. Feeds the banner backward scan.
      // A collapsed member draws no label at all — only its marker-sized
      // footprint blocks banners behind it.
      const drawnReach = collapsedMarkerX.has(a.id)
        ? x + MIN_X_CLEAR
        : limit === undefined
          ? x + fullReach[i]
          : Math.min(x + fullReach[i], limit - 5);
      points.push({ id: a.id, x, y, labelReach: drawnReach });
    }
  }

  points.sort((p, q) => p.x - q.x);

  return { targets, rows, limits, points, groups, fanned, listed };
}

class ActivityLayoutEngine {
  /** False while the Y easing is still travelling (renderer self-loops). */
  settled = true;

  /**
   * Key of the group currently fanned out by a marker click, or null. Reactive
   * so MapCanvas can watch it for repaints; cleared automatically when the
   * cluster dissolves (zoomed apart / data changed) and on reset.
   */
  readonly expandedGroup = ref<string | null>(null);

  /** Marker under the cursor (hover ring); interaction writes, painter reads. */
  readonly hoveredGroup = ref<string | null>(null);

  /**
   * The expanded group whose members do not fit into a fan inside their band:
   * its marker stays drawn and MapGroupList lists the members next to it.
   * Null while the expanded group fans out (or nothing is expanded).
   */
  readonly listedGroup = shallowRef<LayoutGroup | null>(null);

  /** The expanded group whose members are fanned out on the map, or null. */
  readonly fannedGroup = shallowRef<string | null>(null);

  private current = new Map<string, number>();
  private flow: FlowFrame | null = null;
  private epoch = 0;
  private lastTick = 0;
  /** Collapsed markers of the current frame (converged, not expanded). */
  private renderGroups: LayoutGroup[] = [];
  /** The single selection nextExpansion last saw, and whether its reveal is due. */
  private lastSelection = '';
  private revealPending = false;
  /** Members of `renderGroups` — suppressed from hexagons/labels/banners. */
  private collapsedSet = new Set<string>();
  /** Member id → its collapsed marker, rebuilt alongside `collapsedSet`. */
  private collapsedBy = new Map<string, LayoutGroup>();
  /**
   * Reactive motion counter: bumped whenever any animated Y changes. `yOf`
   * touches it so Vue computeds built on it (the canvas popover anchors) track
   * layout motion despite `current` being a plain non-reactive Map.
   */
  private readonly motion = ref(0);

  /**
   * Recompute targets and advance the Y animation; returns the live view for
   * this frame. Called once at the top of every content paint.
   */
  beginFrame(ctx: CanvasRenderingContext2D, scene: SceneContext): LayoutView {
    if (useFeatureFlagStore().canvasActivityLayout === 'classic') {
      this.flow = null;
      if (this.current.size) {
        this.current.clear();
        this.motion.value++;
      }
      this.settled = true;
      this.renderGroups = [];
      this.collapsedSet.clear();
      this.collapsedBy.clear();
      // No groups in this mode: nothing may stay expanded or listed.
      if (this.expandedGroup.value) {
        this.expandedGroup.value = null;
      }
      this.setListedGroup(null);
      this.setFannedGroup(null);

      return classicView(scene);
    }

    // Whether all members are on screen decides only how a group opens (and
    // lets a listed one fan out once they are): a fanned group keeps its fan
    // while the map is panned, instead of closing into the member list as
    // soon as a member reaches the sidebar — also across a carry-over.
    let frame = computeFlowFrame(
      ctx,
      scene,
      this.expandedGroup.value,
      this.isFanned(this.expandedGroup.value),
    );
    const next = this.nextExpansion(scene, frame);
    if (next.key !== this.expandedGroup.value) {
      const keepFan = next.carried && this.isFanned(this.expandedGroup.value);
      this.expandedGroup.value = next.key;
      // Fan or list the moved expansion in this very frame — the detection
      // does not depend on the expanded key, only its fan does.
      frame = computeFlowFrame(ctx, scene, next.key, keepFan);
    }
    this.setListedGroup(frame.listed);
    this.setFannedGroup(frame.fanned);
    if (!this.flow || !sameTargets(this.flow.targets, frame.targets)) {
      this.epoch++;
    }
    this.flow = frame;
    this.animate(frame.targets);

    // A group renders collapsed (marker instead of member hexagons) only once
    // its members' animated Ys have converged onto their settled targets — so
    // a closing fan glides shut before the marker swaps in, and members that
    // are still visibly apart stay individually drawn and hit-testable. Only
    // the fanned group gives up its marker; a listed one keeps it (the list
    // stands next to it), as does an expanded one scrolled out of view.
    this.renderGroups = [];
    this.collapsedSet.clear();
    this.collapsedBy.clear();
    for (const group of frame.groups) {
      if (group.key === frame.fanned) {
        continue;
      }
      let converged = true;
      for (const id of group.memberIds) {
        const cur = this.current.get(id);
        const target = frame.targets.get(id);
        if (
          cur === undefined ||
          target === undefined ||
          Math.abs(cur - target) > GROUP_SETTLE_EPS
        ) {
          converged = false;
          break;
        }
      }
      if (converged) {
        this.renderGroups.push(group);
        for (const id of group.memberIds) {
          this.collapsedSet.add(id);
          this.collapsedBy.set(id, group);
        }
      }
    }

    return this.liveView(scene);
  }

  /**
   * Settled (non-animated) view for the vector PDF export. Pure computation on
   * the export scene's geometry — does not disturb the live animation state.
   * Groups always export collapsed (matching the on-screen resting state), so
   * a transient fan-out never leaks into a PDF.
   */
  staticView(ctx: CanvasRenderingContext2D, scene: SceneContext): LayoutView {
    if (useFeatureFlagStore().canvasActivityLayout === 'classic') {
      return classicView(scene);
    }
    const frame = computeFlowFrame(ctx, scene, null);
    const { map } = scene;
    const collapsedIds = new Set<string>();
    const markerByMember = new Map<string, LayoutGroup>();
    for (const group of frame.groups) {
      for (const id of group.memberIds) {
        collapsedIds.add(id);
        markerByMember.set(id, group);
      }
    }

    return {
      mode: 'flow',
      epoch: frame,
      points: frame.points,
      groups: frame.groups,
      collapsedIds,
      hoveredGroupKey: null,
      listedGroupKey: null,
      markerOf: (id) => markerByMember.get(id) ?? null,
      y: (id) => frame.targets.get(id) ?? storeY(map, id),
      targetY: (id) => frame.targets.get(id) ?? storeY(map, id),
      labelLimitX: (id) => frame.limits.get(id) ?? Infinity,
      rowKey: (id) => frame.rows.get(id) ?? Math.round(storeY(map, id) / 4),
    };
  }

  /**
   * Live screen Y for interaction code running outside the paint cycle
   * (hit-testing, gestures, popover anchoring, todo-drag). Mirrors the store's
   * vertical-move preview and falls back to the store when this activity isn't
   * laid out (hidden workstream/phase, classic mode, no frame yet).
   */
  yOf(id: string, map: MapStore): number {
    // Reactive touch (see `motion`): lets Vue computeds re-evaluate when the
    // layout glides; a no-op for non-reactive callers (painters, hit-tests).
    void this.motion.value;
    if (
      map.isMovingItemVertically &&
      map.movingContext === 'activity' &&
      map.movingTarget === id
    ) {
      return storeY(map, id);
    }

    return this.current.get(id) ?? storeY(map, id);
  }

  /**
   * Raw animated Y if this activity is currently laid out by the flow engine,
   * else undefined (classic mode, SVG mode, hidden activities). Lets callers
   * with their own store fast-path (todo-drag scan) try the layout first.
   */
  peekY(id: string): number | undefined {
    return this.current.get(id);
  }

  /**
   * Collapsed group markers of the last live frame, for interaction code
   * running outside the paint cycle (hit-testing). Stale by at most one frame.
   */
  collapsedGroups(): readonly LayoutGroup[] {
    return this.renderGroups;
  }

  /** True while this activity is hidden behind a collapsed group marker. */
  isCollapsed(id: string): boolean {
    return this.collapsedSet.has(id);
  }

  /**
   * The collapsed marker standing in for this activity, or null while it is
   * drawn individually (visible, expanded fan member, still gliding shut).
   * Reflects the rendered state of the last live frame, like
   * `collapsedGroups()` — enclosure tests resolved through it always match
   * what's on screen.
   */
  markerOf(id: string): LayoutGroup | null {
    return this.collapsedBy.get(id) ?? null;
  }

  /** Drop all animation state (renderer teardown / map switch). */
  reset(): void {
    if (this.current.size) {
      this.motion.value++;
    }
    this.current.clear();
    this.flow = null;
    this.settled = true;
    this.lastTick = 0;
    this.renderGroups = [];
    this.collapsedSet.clear();
    this.collapsedBy.clear();
    this.expandedGroup.value = null;
    this.hoveredGroup.value = null;
    this.listedGroup.value = null;
    this.fannedGroup.value = null;
    this.lastSelection = '';
    this.revealPending = false;
  }

  /**
   * Which group should be expanded after this frame:
   *  - REVEAL: an activity selected from outside the map (search, link,
   *    notification, focus) that is hidden in a collapsed group opens that
   *    group. Decided once per selection, on the first frame the activity is
   *    laid out — one selected on the map, visible, never pops open later
   *    because zooming out merged it into a group; and closing the group again
   *    (Escape) keeps it closed.
   *  - CARRY-OVER: the expanded cluster changed shape (a zoom step split or
   *    grew it, a member moved): the expansion moves on to the group holding
   *    the selected activity, else to the one sharing most (≥ 2) members — so
   *    one wheel notch doesn't close an open group. Only when none qualifies
   *    is the expansion dropped; zooming back out then can't surprise-reopen.
   * `carried` tells a carry-over (the same group, reshaped) from a reveal.
   */
  private nextExpansion(
    scene: SceneContext,
    frame: FlowFrame,
  ): { key: string | null; carried: boolean } {
    const { map } = scene;
    const selected =
      (map.selectedContext === 'activity' ||
        map.selectedContext === 'mirroredActivity') &&
      !map.selectedIds.length
        ? map.selectedId
        : '';
    if (selected !== this.lastSelection) {
      this.lastSelection = selected;
      this.revealPending = !!selected;
    }
    if (this.revealPending && frame.targets.has(selected)) {
      this.revealPending = false;
      const group = frame.groups.find((g) => g.memberIds.includes(selected));
      if (group) {
        return { key: group.key, carried: false };
      }
    }
    const expanded = this.expandedGroup.value;
    if (!expanded || frame.groups.some((g) => g.key === expanded)) {
      return { key: expanded, carried: false };
    }
    const old = new Set(expanded.split('|'));
    let successor: LayoutGroup | null = null;
    let shared = 1;
    for (const group of frame.groups) {
      if (old.has(selected) && group.memberIds.includes(selected)) {
        return { key: group.key, carried: true };
      }
      const n = group.memberIds.filter((id) => old.has(id)).length;
      if (n > shared) {
        successor = group;
        shared = n;
      }
    }

    return { key: successor?.key ?? null, carried: !!successor };
  }

  /** Whether this expanded group was fanned out in the last frame. */
  private isFanned(key: string | null): boolean {
    return key !== null && this.fannedGroup.value === key;
  }

  /** Publish the fanned group only when it changes (Escape closes a fan). */
  private setFannedGroup(key: string | null): void {
    if (this.fannedGroup.value !== key) {
      this.fannedGroup.value = key;
    }
  }

  /**
   * Publish the listed group only when it changes (membership or position),
   * so the list does not re-render on every animation frame.
   */
  private setListedGroup(group: LayoutGroup | null): void {
    const current = this.listedGroup.value;
    if (
      current?.key === group?.key &&
      current?.pos === group?.pos &&
      current?.y === group?.y
    ) {
      return;
    }
    this.listedGroup.value = group;
  }

  private liveView(scene: SceneContext): LayoutView {
    const { map } = scene;
    const flow = this.flow;

    return {
      mode: 'flow',
      epoch: this.epoch,
      points: flow?.points ?? [],
      groups: this.renderGroups,
      collapsedIds: this.collapsedSet,
      hoveredGroupKey: this.hoveredGroup.value,
      listedGroupKey: this.listedGroup.value?.key ?? null,
      markerOf: (id) => this.collapsedBy.get(id) ?? null,
      y: (id) => this.yOf(id, map),
      targetY: (id) => flow?.targets.get(id) ?? this.yOf(id, map),
      labelLimitX: (id) => flow?.limits.get(id) ?? Infinity,
      rowKey: (id) => flow?.rows.get(id) ?? Math.round(storeY(map, id) / 4),
    };
  }

  /** Ease every current Y toward its target; prune activities that left. */
  private animate(targets: Map<string, number>): void {
    const now = performance.now();
    const dt = this.lastTick ? Math.min(now - this.lastTick, 100) : 0;
    this.lastTick = now;
    const blend = 1 - Math.exp(-dt / ANIM_TAU_MS);

    let moving = false;
    let changed = false;
    for (const [id, target] of targets) {
      const cur = this.current.get(id);
      if (cur === undefined) {
        // First sight: appear in place, no fly-in.
        this.current.set(id, target);
        changed = true;
        continue;
      }
      const delta = target - cur;
      if (Math.abs(delta) <= SNAP_EPS) {
        if (cur !== target) {
          this.current.set(id, target);
          changed = true;
        }
        continue;
      }
      this.current.set(id, cur + delta * blend);
      moving = true;
      changed = delta * blend !== 0 || changed;
    }
    // Prune by MEMBERSHIP, not by a size comparison: layout membership can
    // change at constant size (WI #5596 — a filter edit hides one activity
    // and reveals another), and a stale entry would keep anchoring that
    // activity's links at a spot where nothing is drawn anymore.
    for (const id of this.current.keys()) {
      if (!targets.has(id)) {
        this.current.delete(id);
        changed = true;
      }
    }
    this.settled = !moving;
    if (changed) {
      this.motion.value++;
    }
  }
}

/** Targets unchanged ⇒ derived caches (stagger memo) can keep their epoch. */
function sameTargets(a: Map<string, number>, b: Map<string, number>): boolean {
  if (a.size !== b.size) {
    return false;
  }
  for (const [id, y] of b) {
    if (a.get(id) !== y) {
      return false;
    }
  }

  return true;
}

const NO_GROUPS: readonly LayoutGroup[] = [];
const NO_COLLAPSED: ReadonlySet<string> = new Set();

/** Legacy parity: every read delegates to the store lane getters. */
function classicView(scene: SceneContext): LayoutView {
  const { map, io } = scene;

  return {
    mode: 'classic',
    epoch: map.activityLanes,
    points: [],
    groups: NO_GROUPS,
    collapsedIds: NO_COLLAPSED,
    hoveredGroupKey: null,
    listedGroupKey: null,
    markerOf: () => null,
    y: (id) => storeY(map, id),
    targetY: (id) => storeY(map, id),
    labelLimitX: (id) => {
      const nextId = map.activityLanes[id]?.nextId;

      return nextId
        ? scene.screenX(map.activityPos(io.activity(nextId)))
        : Infinity;
    },
    rowKey: (id) => map.activityLanes[id]?.number ?? 0,
  };
}

/**
 * Painter-side helper: the scene's layout view, falling back to the store
 * before the first content frame has run (e.g. an early overlay paint).
 */
export function layoutY(scene: SceneContext, id: string): number {
  return scene.layout ? scene.layout.y(id) : scene.map.getActivityY(id);
}

/**
 * Painter-side helper: the screen point connectors (relationship links, link
 * line) anchor to for this activity. A collapsed group member is invisible —
 * the user sees only its marker — so it anchors at the marker's centre (mean
 * member position, the GroupMarkersPainter geometry); everything else anchors
 * at its own (possibly animated) position. Goes through `scene.layout`, so
 * the export view's always-collapsed groups resolve correctly too.
 */
export function layoutAnchor(
  scene: SceneContext,
  a: MapActivity,
): { x: number; y: number } {
  const layout = scene.layout;
  const group = layout?.markerOf(a.id) ?? null;
  if (group && layout) {
    return {
      x: scene.screenX(group.pos),
      y: markerY(group, (id) => layout.y(id)),
    };
  }

  return {
    x: scene.screenX(scene.map.activityPos(a)),
    y: layoutY(scene, a.id),
  };
}

/** The singleton live engine — one map view exists at a time. */
export const activityLayout = new ActivityLayoutEngine();
